import { ref, watch, onScopeDispose } from 'vue'
import axios from 'axios'

// Single characters match almost everything, so suggestions start from the second one
export const MIN_QUERY_LENGTH = 2

/**
 * Suggestions from /api/autocomplete for the given query ref.
 * Every new request aborts the previous one, so a slow answer can never overwrite a newer one.
 */
export function useSearchSuggestions (query) {
    const categories = ref([])
    const bundles = ref([])
    const loading = ref(false)
    const error = ref(false)
    // The query the current results belong to; lags behind `query` while typing
    const resultsFor = ref('')

    let controller = null

    async function fetchSuggestions (value) {
        controller?.abort()
        controller = new AbortController()

        loading.value = true
        error.value = false

        try {
            const { data } = await axios.get('/api/autocomplete', { params: { q: value }, signal: controller.signal })

            categories.value = data.categories ?? []
            bundles.value = data.bundles ?? []
            resultsFor.value = value

            if (bundles.value.length === 0) {
                window.plausible?.('search-query', { props: { value } })
            }
        } catch (e) {
            if (axios.isCancel(e)) return
            error.value = true
        }

        loading.value = false
    }

    // A plain timer rather than useDebounceFn, because a pending fetch must be cancellable
    let timer = null

    watch(query, value => {
        const trimmed = value.trim()

        clearTimeout(timer)

        if (trimmed.length < MIN_QUERY_LENGTH) {
            controller?.abort()
            categories.value = []
            bundles.value = []
            resultsFor.value = ''
            loading.value = false
            error.value = false
            return
        }

        loading.value = true
        timer = setTimeout(() => fetchSuggestions(trimmed), 200)
    })

    onScopeDispose(() => {
        clearTimeout(timer)
        controller?.abort()
    })

    return { categories, bundles, loading, error, resultsFor }
}
